import { useEffect, useState } from 'react';
import * as R from 'ramda';

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined as undefined | number,
    height: undefined as undefined | number
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth as number,
        height: window.innerHeight
      });
    }

    // Only run on client
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return windowSize;
}

const getSuccessfullRepetionsCount = (valid: number[]) => {
  let successfullRepetiotionCount = 0;
  for (let i = 0; i < valid.length; i += 4) {
    let succesfullPhases = 0;
    for (let j = 0; j < 4; j++) {
      if (valid[i + j]) succesfullPhases += 1;
    }
    if (succesfullPhases > 2) successfullRepetiotionCount += 1;
  }
  return successfullRepetiotionCount;
};

export function getBestLinearSegmentPercentage(
  arr: number[],
  threshold = 6,
  minLength = 3
): number {
  const maxOutliers = (arr.length * 7) / 100;
  const n = arr.length;
  if (n < minLength) return 0;

  let bestLength = 0;

  // Precompute cumulative sums for O(1) regression stats
  const sumX = Array(n + 1).fill(0);
  const sumY = Array(n + 1).fill(0);
  const sumXY = Array(n + 1).fill(0);
  const sumXX = Array(n + 1).fill(0);

  for (let i = 0; i < n; i++) {
    sumX[i + 1] = sumX[i] + i;
    sumY[i + 1] = sumY[i] + arr[i];
    sumXY[i + 1] = sumXY[i] + i * arr[i];
    sumXX[i + 1] = sumXX[i] + i * i;
  }

  for (let i = 0; i <= n - minLength; i++) {
    for (let j = i + minLength; j <= n; j++) {
      const len = j - i;
      const sumXij = sumX[j] - sumX[i];
      const sumYij = sumY[j] - sumY[i];
      const sumXYij = sumXY[j] - sumXY[i];
      const sumXXij = sumXX[j] - sumXX[i];

      const denom = len * sumXXij - sumXij * sumXij;
      if (denom === 0) continue;

      const a = (len * sumXYij - sumXij * sumYij) / denom;
      const b = (sumYij - a * sumXij) / len;

      let outlierCount = 0;
      for (let k = i; k < j; k++) {
        const x = k - i;
        const predicted = a * x + b;
        const actual = arr[k];
        const deviation = Math.abs(predicted - actual);

        if (deviation > threshold) {
          outlierCount++;
          if (outlierCount > maxOutliers) {
            break; // too many jumps -> reject this segment
          }
        }
      }

      if (outlierCount <= maxOutliers) {
        bestLength = Math.max(bestLength, len);
      }
    }
  }

  const percentage = (bestLength / n) * 100;
  return parseFloat(percentage.toFixed(2));
}

export function getCorrectnessData(data: number[][], pattern: number[], dbData: number[][]) {
  let correctFrequency = 0;
  let inhaleCorrectness = 0;
  let exhaleCorrectness = 0;
  let inhaleStopCorrectness = 0;
  let exhaleStopCorrectness = 0;
  let patternCorrectness = 0;
  const valid = Array(pattern.length);
  for (const i in valid) {
    valid[i] = false;
  }
  // setFrequencyData(dataRef.current)
  // setDbData(dbDataRef.current)
  const inhaleCorrect = (index: number) => {
    return (
      R.median(data[index]) > 159 &&
      R.median(data[index]) < 172 &&
      getBestLinearSegmentPercentage(data[index]) > 80
    );
  };
  const exhaleCorrect = (index: number) => {
    return (
      R.median(data[index]) > 127 &&
      R.median(data[index]) < 134 &&
      getBestLinearSegmentPercentage(data[index]) > 80
    );
  };
  for (let i = 0; i < pattern.length; i++) {
    if (i % 4 === 0) {
      if (inhaleCorrect(i)) {
        valid[i] = true;

        correctFrequency += 1;
        patternCorrectness += 1;
        inhaleCorrectness += 1;
      } else {
        valid[i] = false;
      }
    } else if (i % 4 === 2) {
      if (exhaleCorrect(i)) {
        valid[i] = true;
        correctFrequency += 1;
        exhaleCorrectness += 1;
        patternCorrectness += 1;
      } else {
        valid[i] = false;
      }
    } else {
      if (
        getBestLinearSegmentPercentage(data[i]) < 30 &&
        R.median(dbData[i - 1]) - R.median(dbData[i]) > 15
      ) {
        //look at previous step
        if ((i - 1) % 4 === 0) {
          if (inhaleCorrect(i - 1)) {
            patternCorrectness += 1;
            inhaleStopCorrectness += 1;
            valid[i] = true;
          } else {
            valid[i] = false;
          }
        } else if ((i - 1) % 4 === 2) {
          if (exhaleCorrect(i - 1)) {
            patternCorrectness += 1;
            exhaleStopCorrectness += 1;
            valid[i] = true;
          } else {
            valid[i] = false;
          }
        }
      } else {
        valid[i] = false;
      }
    }
  }
  console.log(valid);
  return {
    patternPercentage: patternCorrectness / pattern.length,
    inhalePercentage: inhaleCorrectness / (pattern.length / 4),
    exhalePercentage: exhaleCorrectness / (pattern.length / 4),
    repetitionsSuccess: getSuccessfullRepetionsCount(valid),
    frequencyPercentage: correctFrequency / pattern.length / 2,
    inhaleStopPercentage: inhaleStopCorrectness / (pattern.length / 4),
    exhaleStopPercentage: exhaleStopCorrectness / (pattern.length / 4),
    valid
  };
}
